import React, { useEffect, useRef } from "react";
import { Box, GlobalStyles } from "@mui/material";

interface MagicLoaderProps {
  isLoading: boolean;
  // "overlay" = fixed full-screen with a blurred backdrop; "inline" = fills its positioned parent.
  variant?: "overlay" | "inline";
}

// Ripple diameters relative to the 320px stage.
const RIPPLE_SIZES = [80, 140, 210, 290];
const CYCLE = 3600;

// Ripple keyframes/classes are injected globally (prefixed `bsml-`) because the ripple
// elements are created imperatively at runtime — they can't carry Emotion `sx` classes.
const rippleGlobalStyles = (
  <GlobalStyles
    styles={{
      "@keyframes bsml-ripple-out": {
        "0%": { transform: "translate(-50%,-50%) scale(0)", opacity: 0.9 },
        "70%": { opacity: 0.18 },
        "100%": { transform: "translate(-50%,-50%) scale(1)", opacity: 0 },
      },
      "@keyframes bsml-ripple-in": {
        "0%": { transform: "translate(-50%,-50%) scale(1)", opacity: 0 },
        "18%": { opacity: 0.6 },
        "80%": { opacity: 0.28 },
        "100%": { transform: "translate(-50%,-50%) scale(0)", opacity: 0 },
      },
      ".bsml-ripple": {
        position: "absolute", left: "50%", top: "50%", borderRadius: "50%",
        border: "1px solid rgba(70,160,235,0.55)",
        transform: "translate(-50%,-50%) scale(0)", opacity: 0, pointerEvents: "none",
        animation: "bsml-ripple-out var(--dur,1200ms) cubic-bezier(0.15,0,0.55,1) forwards",
      },
      ".bsml-ripple-in": {
        position: "absolute", left: "50%", top: "50%", borderRadius: "50%",
        border: "1px solid rgba(70,160,235,0.55)", opacity: 0, pointerEvents: "none",
        animation: "bsml-ripple-in var(--dur,900ms) cubic-bezier(0.4,0,0.82,1) forwards",
      },
    }}
  />
);

const MagicLoader: React.FC<MagicLoaderProps> = ({ isLoading, variant = "overlay" }) => {
  const ringsRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const haloRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading) return;
    const rings = ringsRef.current;
    const dot = dotRef.current;
    const logo = logoRef.current;
    const halo = haloRef.current;
    if (!rings || !dot || !logo || !halo) return;

    // Honor reduced-motion: show a static logo + halo, skip the looping animation.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      logo.style.opacity = "1";
      logo.style.transform = "scale(1)";
      halo.style.opacity = "1";
      return;
    }

    // Every timer is tracked so the recursive loop and pending ripple removals
    // are fully cancelled on unmount/!isLoading — otherwise the loop leaks.
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const after = (fn: () => void, ms: number) => {
      const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
      timers.add(id);
      return id;
    };

    const animate = (el: HTMLElement, keyframes: Keyframe[], options: KeyframeAnimationOptions) =>
      el.animate(keyframes, { fill: "forwards", ...options });

    const spawnRipple = (size: number, delay: number, dur = 1100, inward = false) => {
      after(() => {
        const el = document.createElement("div");
        el.className = inward ? "bsml-ripple-in" : "bsml-ripple";
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.setProperty("--dur", `${dur}ms`);
        rings.appendChild(el);
        after(() => el.remove(), dur + 50);
      }, delay);
    };

    const runCycle = () => {
      // P1: dot scales in + glow
      animate(dot, [
        { opacity: 0, transform: "scale(0)", boxShadow: "0 0 0px 0px rgba(70,160,235,0)" },
        { opacity: 1, transform: "scale(1)", boxShadow: "0 0 18px 7px rgba(70,160,235,0.65)" },
      ], { duration: 380, easing: "cubic-bezier(0.34,1.4,0.64,1)" });

      after(() => {
        animate(dot, [
          { boxShadow: "0 0 18px 7px rgba(70,160,235,0.65)" },
          { boxShadow: "0 0 30px 14px rgba(70,160,235,0.2)" },
          { boxShadow: "0 0 18px 7px rgba(70,160,235,0.65)" },
        ], { duration: 900, easing: "ease-in-out", iterations: 2 });
      }, 380);

      // P2: ripple burst (staggered)
      RIPPLE_SIZES.forEach((size, i) => spawnRipple(size, 180 + i * 110, 1050));

      // P3: dot out, halo blooms, logo scales in
      after(() => {
        animate(dot, [
          { opacity: 1, transform: "scale(1)" },
          { opacity: 0, transform: "scale(0.5)" },
        ], { duration: 320, easing: "ease-in" });
        animate(halo, [{ opacity: 0 }, { opacity: 1 }], { duration: 500, easing: "ease-out" });
        animate(logo, [
          { opacity: 0, transform: "scale(0.78)", filter: "drop-shadow(0 0 0px rgba(70,160,235,0))" },
          { opacity: 1, transform: "scale(1)", filter: "drop-shadow(0 0 12px rgba(70,160,235,0.35))" },
        ], { duration: 520, easing: "cubic-bezier(0.34,1.1,0.64,1)" });
      }, 680);

      // P4: logo breathes
      after(() => {
        animate(logo, [
          { filter: "drop-shadow(0 0 12px rgba(70,160,235,0.35))" },
          { filter: "drop-shadow(0 0 22px rgba(70,160,235,0.65))" },
          { filter: "drop-shadow(0 0 12px rgba(70,160,235,0.35))" },
        ], { duration: 1000, easing: "ease-in-out" });
        animate(halo, [{ opacity: 1 }, { opacity: 0.55 }, { opacity: 1 }], { duration: 1000, easing: "ease-in-out" });
      }, 1250);

      // P5: logo + halo out, ripples collapse inward, dot re-emerges
      after(() => {
        animate(logo, [
          { opacity: 1, transform: "scale(1)", filter: "drop-shadow(0 0 12px rgba(70,160,235,0.35))" },
          { opacity: 0, transform: "scale(0.88)", filter: "drop-shadow(0 0 0px rgba(70,160,235,0))" },
        ], { duration: 420, easing: "ease-in" });
        animate(halo, [{ opacity: 1 }, { opacity: 0 }], { duration: 380, easing: "ease-in" });

        const inSizes = [...RIPPLE_SIZES].reverse();
        inSizes.forEach((size, i) => spawnRipple(size, i * 90, 750, true));

        after(() => {
          animate(dot, [
            { opacity: 0, transform: "scale(0)", boxShadow: "0 0 0px 0px rgba(70,160,235,0)" },
            { opacity: 1, transform: "scale(1)", boxShadow: "0 0 22px 9px rgba(70,160,235,0.7)" },
          ], { duration: 300, easing: "cubic-bezier(0.34,1.5,0.64,1)" });
        }, inSizes.length * 90 + 200);
      }, 2450);

      after(runCycle, CYCLE);
    };

    after(runCycle, 200);

    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
      if (rings) rings.innerHTML = "";
    };
  }, [isLoading]);

  if (!isLoading) return null;

  const wrapperSx =
    variant === "overlay"
      ? {
          position: "fixed" as const, inset: 0, zIndex: 9999,
          backgroundColor: "rgba(8,11,17,0.55)",
          backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        }
      : { position: "absolute" as const, inset: 0, zIndex: 1 };

  return (
    <>
      {rippleGlobalStyles}
      <Box sx={{ ...wrapperSx, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ position: "relative", width: 320, height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box ref={ringsRef} sx={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
          <Box
            ref={haloRef}
            sx={{
              position: "absolute", width: 260, height: 100, borderRadius: "50%",
              background: "radial-gradient(ellipse at center, rgba(70,160,235,0.18) 0%, transparent 70%)",
              filter: "blur(20px)", opacity: 0, pointerEvents: "none",
            }}
          />
          <Box
            ref={dotRef}
            sx={{ position: "absolute", width: 10, height: 10, borderRadius: "50%", bgcolor: "#46A0EB", opacity: 0, transform: "scale(0)" }}
          />
          <Box
            ref={logoRef}
            sx={{ position: "absolute", width: 200, opacity: 0, transform: "scale(0.82)", pointerEvents: "none", "& svg": { width: "100%", height: "auto", display: "block" } }}
          >
            <svg viewBox="0 0 3021 721" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 194.85H338.74C462.266 194.85 524.029 238.318 524.029 325.255V346.054C524.029 402.502 500.736 438.949 454.149 455.396C478.415 460.195 500.426 472.895 516.769 491.527C532.954 510.048 541.033 534.198 541.008 563.979V584.321C541.008 625.007 525.693 657.089 495.064 680.57C464.434 704.05 417.986 715.802 355.718 715.828H0V194.85ZM185.29 324.496V401.161H296.388C311.11 401.161 321.547 398.795 327.698 394.064C333.824 389.358 336.925 381.539 336.925 370.647V355.01C336.925 344.118 333.824 336.3 327.698 331.594C321.572 326.887 311.098 324.496 296.388 324.496H185.29ZM185.29 509.744V586.409H314.841C329.551 586.409 340.063 584.018 346.34 579.311C352.618 574.605 355.718 566.787 355.718 555.895V540.258C355.718 529.353 352.592 521.547 346.34 516.841C340.088 512.135 329.589 509.769 314.841 509.744H185.29Z" fill="#1778C7" />
              <path d="M603.211 198.649H927.127C1072.81 198.649 1145.67 257.008 1145.69 373.725V417.675C1145.69 534.063 1072.84 592.258 927.127 592.258H788.501V719.399H603.211V198.649ZM788.501 341.769V449.631H892.754C915.897 449.631 932.724 446.114 943.236 439.08C953.749 432.046 958.992 420.913 958.967 405.682V385.339C958.967 369.348 953.925 358.089 943.841 351.561C933.758 345.033 916.754 341.769 892.83 341.769H788.501Z" fill="#1778C7" />
              <path d="M1204.34 199.136H1531.13C1610.27 199.136 1666.48 212.571 1699.79 239.442C1733.09 266.312 1749.73 311.236 1749.7 374.212V393.454C1749.7 446.968 1727.26 488.615 1682.39 518.395C1723.43 538.257 1743.94 575.185 1743.91 629.179V720H1565.89V653.165C1566.25 639.062 1565.27 624.956 1562.97 611.038C1561.87 606.262 1559.79 601.771 1556.86 597.85C1553.93 593.928 1550.22 590.664 1545.96 588.266C1534.97 581.839 1522.65 578.059 1509.96 577.222C1495.36 575.729 1473.23 574.995 1443.56 575.02H1389.63V720H1204.34V199.136ZM1389.63 340.776V447.044H1497.1C1521.71 447.044 1538.84 444.071 1548.49 438.125C1558.15 432.179 1562.97 421.109 1562.97 404.916V383.093C1562.97 366.647 1558.15 355.514 1548.49 349.695C1538.84 343.875 1521.71 340.966 1497.1 340.966L1389.63 340.776Z" fill="#1778C7" />
              <path d="M6.00781 45.1637H29.2636V6.14819H75.6238V45.1637H116.841V80.6495H75.6238V120.007C75.174 124.01 76.152 128.044 78.3842 131.392C80.2371 133.328 83.9807 134.277 89.7285 134.277H118.808V167.03C106.497 169.933 93.8606 171.209 81.2203 170.825C62.3132 170.825 49.116 167.03 41.175 159.098C33.234 151.166 29.2257 139.173 29.2257 122.777V80.6495H6.00781V45.1637Z" fill="#1778C7" />
              <path d="M140.703 0H187.139V57.878C194.903 48.7693 206.248 44.215 221.172 44.215H234.066C251.461 44.215 264.167 48.3898 272.183 56.7394C280.2 65.089 284.195 77.4489 284.17 93.8192V170.066H237.734V101.524C237.139 93.1192 234.763 87.944 232.744 85.9403C230.726 83.9367 228.256 82.4511 225.543 81.6094C222.831 80.7677 219.957 80.5947 217.163 81.1051H209.601C194.626 81.1051 187.063 88.8981 186.912 104.484V170.066H140.703V0Z" fill="#1778C7" />
              <path d="M372.313 43.9872H393.262C413.253 43.9872 428.378 48.7692 438.639 58.3333C448.899 67.8974 454.054 80.8646 454.105 97.2349V117.881H355.788C355.788 125.927 357.83 131.62 361.384 134.96C366.311 138.693 372.44 140.464 378.59 139.932H390.426C403.182 139.932 409.56 136.301 409.56 129.039V126.534H454.105V131.392C454.321 137.106 453.105 142.782 450.568 147.901C448.031 153.02 444.254 157.418 439.584 160.692C429.878 167.726 415.345 171.243 395.984 171.243H373.976C352.725 171.243 336.629 166.486 325.688 156.973C314.747 147.459 309.301 133.998 309.352 116.591V98.6771C309.352 81.5225 314.684 68.1125 325.347 58.4472C336.011 48.7819 351.666 43.9619 372.313 43.9872ZM379.044 74.8427C364.699 74.8427 357.023 81.8007 356.015 95.7168H409.786C409.786 81.8007 402.854 74.8427 388.989 74.8427H379.044Z" fill="#1778C7" />
              <path d="M1789.64 646.546H1867.14V720.14H1789.64V646.546ZM2156.08 191.814H2275.45C2310.09 191.814 2337.23 199.306 2356.88 214.288C2376.53 229.025 2392.12 253.465 2403.67 287.606L2544.04 720.14H2348.03L2320.03 618.454L2110.77 619.191L2083.87 720.14H1887.5L2027.87 287.606C2039.41 253.465 2055.01 229.025 2074.66 214.288C2094.31 199.306 2121.45 191.814 2156.08 191.814ZM2178.93 363.133L2147.24 474.767H2284.66L2252.98 363.133C2249.54 351.589 2242.42 345.817 2231.61 345.817H2199.93C2189.36 345.817 2182.36 351.589 2178.93 363.133ZM2589.35 191.814H2777.99V720.14H2589.35V191.814Z" fill="#1778C7" />
            </svg>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MagicLoader;
